<template>
    <!-- @vue-expect-error ListingComponent required props provided dynamically via v-bind -->
    <ListingComponent v-bind="listingProps" @listinghash="storeRows" @verify-all="verifyAll" />
</template>
<script setup lang="ts">
import { ref, computed, onUnmounted, inject } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import ListingComponent from 'vcomponents/listing/Listing.vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { goToUrl } from 'js/vue/helpers';
import OcrListing from 'stores/ocrListing';
import Ocr from 'api/ocr';
import { cloneDeep } from 'lodash';
import { NON_VERIFIABLE_OCR_STATES } from 'types/entities/ocr/ocr.type';
import type { OcrListingRow } from 'types/entities/ocr/ocr.type';
import { OcrEvent, EInvoicingEvent, ListingEvent } from 'types/events';
import type { OcrEventPayload } from 'types/events';
import type { Translator } from 'types/injects/translator.type';
import type { ListingButton, ListingConfig } from 'types/components/listing/listing.type';

defineOptions({ inheritAttrs: false });

const store = useStore();
const route = useRoute();
const $t = inject('$t') as Translator;

// --- Refs ---

const hash = ref<string | null>(null);
const ocrLocalStorage = new LocalStorage('ocr', 'form.verification');

// --- Computed ---

// Listing config is dynamically injected into route params by OcrListingResolver
const listingData = computed(() => route.params as unknown as ListingConfig);

const rows = computed<OcrListingRow[]>(() => {
    return hash.value ? store.state[hash.value].rows : [];
});

const canValidatePurInvoice = computed<boolean>(() => {
    return hash.value ? store.state[hash.value].metadatas.user_can_validate_pur_invoice : false;
});

const customButtons = computed((): ListingButton[] => {
    const baseButtons = listingData.value.buttons ? cloneDeep(listingData.value.buttons) : [];

    if (!canValidatePurInvoice.value) {
        return baseButtons;
    }

    return [
        {
            label: $t.__('ocr.OcrList.check_all'),
            event: 'verify-all',
            primary: false,
            disabled: !rows.value.length,
            databot: 'purchases-purinvoices-list_verify-all',
        },
        ...baseButtons,
    ];
});

const listingProps = computed(
    (): ListingConfig => ({
        ...listingData.value,
        buttons: customButtons.value,
        storeModule: OcrListing,
    }),
);

// --- Event handlers ---

function storeRows(newHash: string): void {
    hash.value = newHash;
}

function verifySingle({ params }: OcrEventPayload): void {
    const targetRow = rows.value.find((row) => row.id === params.id);
    if (targetRow && NON_VERIFIABLE_OCR_STATES.includes(targetRow.state)) {
        return;
    }
    goToUrl(`/purchase/incoming/verification/${params.id}`);
}

function verifyAll(): void {
    if (!hash.value) {
        return;
    }
    const verifiableRows: OcrListingRow[] = store.state[hash.value].rows.filter(
        (row: OcrListingRow) => !NON_VERIFIABLE_OCR_STATES.includes(row.state),
    );
    if (!verifiableRows.length) {
        return;
    }
    ocrLocalStorage.set({
        rows: verifiableRows,
        multipleVerify: true,
        totalRows: verifiableRows.length,
    });
    goToUrl(`/purchase/incoming/verification/${verifiableRows[0].id}`);
}

function deleteOcr({ params }: OcrEventPayload): void {
    Ocr.deleteOcr(params.id).then(() => {
        EventBus.emit(ListingEvent.REFRESH);
    });
}

// --- Lifecycle ---

if (ocrLocalStorage.get()) {
    ocrLocalStorage.delete();
}

const debouncedRefresh = _.debounce(() => {
    EventBus.emit(ListingEvent.REFRESH);
}, 1000);

EventBus.on(OcrEvent.ADDED, debouncedRefresh);
EventBus.on(OcrEvent.COMPLETED, debouncedRefresh);
EventBus.on(OcrEvent.FAILED, debouncedRefresh);
EventBus.on(OcrEvent.VERIFY, verifySingle);
EventBus.on(OcrEvent.DELETE, deleteOcr);
EventBus.on(EInvoicingEvent.RECEIVED, debouncedRefresh);

onUnmounted(() => {
    debouncedRefresh.cancel();
    EventBus.off(OcrEvent.ADDED, debouncedRefresh);
    EventBus.off(OcrEvent.COMPLETED, debouncedRefresh);
    EventBus.off(OcrEvent.FAILED, debouncedRefresh);
    EventBus.off(OcrEvent.VERIFY, verifySingle);
    EventBus.off(OcrEvent.DELETE, deleteOcr);
    EventBus.off(EInvoicingEvent.RECEIVED, debouncedRefresh);
});
</script>
